import type { QueryClient } from '@tanstack/react-query';
import {
  AuditQuestionCompletionStatus,
  ResponseResult,
  type GetAuditSummaryCommandOutput,
  type QuestionResponseDetails,
  type SubQuestionResponseEntry,
} from '@amzn/gbsa-helios-typescript-client';
import type { CacheSnapshot } from './patchAuditSummaryCache';
import {
  deriveParentCompletion,
  deriveParentResult,
  scoredSubQuestionIds,
  type SubQuestionTypeRef,
} from './deriveParentResult';
import {
  EMPTY_RESPONSE,
  applyMetricsDiff,
  auditSummaryPredicate,
  buildNextCacheData,
} from './patchAuditCachePlumbing';

/**
 * Optimistic cache patches for ANSWER saves (flat, per-sub, bulk).
 * Field saves (comment / naReason / mediaIds) live in
 * patchAuditFieldCaches. All patches snapshot the whole summary
 * output for rollbackAuditQuestionResponseCache.
 */

export interface QuestionResponsePatch {
  readonly responseResult: ResponseResult;
  readonly respondedBy: string;
  /** Defaults to `new Date()` at patch time. */
  readonly respondedAt?: Date;
}

export interface SubQuestionResponsePatch {
  readonly subQuestionId: string;
  readonly result: ResponseResult;
  readonly respondedBy: string;
  readonly respondedAt?: Date;
  /** Child id + responseType list from the full-question fetch — the
   *  summary cache only carries answered children, and parent
   *  derivation needs each child's ERT type to split scored from
   *  data-collection subs. */
  readonly subQuestionRefs: readonly SubQuestionTypeRef[];
}

/**
 * Patch a flat question's answer: overwrite result, flip status to
 * COMPLETED, stamp responder, diff-update audit.metrics.
 */
export function snapshotAndPatchAuditQuestionResponse(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  patch: QuestionResponsePatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });

  const snapshots: CacheSnapshot[] = [];
  const now = patch.respondedAt ?? new Date();

  for (const [queryKey, cachedData] of matchingQueries) {
    if (!cachedData?.questions || !cachedData.audit) continue;
    const targetIndex = cachedData.questions.findIndex(
      (q) => q.questionDetails.questionId === questionId,
    );
    if (targetIndex < 0) continue;

    const previousResponse =
      cachedData.questions.at(targetIndex)?.responseDetails?.mainQuestionResponse;
    const previousResult = previousResponse?.responseResult;

    snapshots.push({ queryKey, data: cachedData });

    const nextResponse: QuestionResponseDetails = {
      ...(previousResponse ?? EMPTY_RESPONSE),
      responseStatus: AuditQuestionCompletionStatus.COMPLETED,
      responseResult: patch.responseResult,
      respondedBy: patch.respondedBy,
      respondedAt: now,
    };
    // Non-NA answers clear the stale reason label (cache/wire parity —
    // the picker visibility rule reads this slot).
    if (patch.responseResult !== ResponseResult.NA) {
      delete (nextResponse as Partial<QuestionResponseDetails>).naReason;
    }

    const nextQuestions = cachedData.questions.map((q, i) =>
      i === targetIndex
        ? {
            ...q,
            responseDetails: {
              ...q.responseDetails,
              mainQuestionResponse: nextResponse,
            },
          }
        : q,
    );

    const nextMetrics = applyMetricsDiff(
      cachedData.audit.metrics,
      previousResult,
      patch.responseResult,
    );
    queryClient.setQueryData(queryKey, {
      ...cachedData,
      audit: nextMetrics
        ? { ...cachedData.audit, metrics: nextMetrics }
        : cachedData.audit,
      questions: nextQuestions,
    });
  }

  return snapshots;
}

/**
 * Patch one sub-question's answer, then independently re-derive the
 * parent's aggregate result and all-children-answered completion.
 * FAIL may determine the aggregate before the parent is complete.
 */
export function snapshotAndPatchAuditSubQuestionResponse(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  patch: SubQuestionResponsePatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });
  const snapshots: CacheSnapshot[] = [];
  const now = patch.respondedAt ?? new Date();

  for (const [queryKey, cachedData] of matchingQueries) {
    const nextData = computeSubQuestionResponsePatch(cachedData, questionId, patch, now);
    if (!nextData) continue;
    snapshots.push({ queryKey, data: cachedData });
    queryClient.setQueryData(queryKey, nextData);
  }
  return snapshots;
}

function computeSubQuestionResponsePatch(
  cachedData: GetAuditSummaryCommandOutput | undefined,
  questionId: string,
  patch: SubQuestionResponsePatch,
  now: Date,
): GetAuditSummaryCommandOutput | null {
  if (!cachedData?.questions || !cachedData.audit) return null;
  const targetIndex = cachedData.questions.findIndex(
    (q) => q.questionDetails.questionId === questionId,
  );
  if (targetIndex < 0) return null;
  const targetQuestion = cachedData.questions.at(targetIndex);
  if (!targetQuestion) return null;

  const previousMainResponse = targetQuestion.responseDetails?.mainQuestionResponse;
  const previousMainResult = previousMainResponse?.responseResult;
  const previousSubResponses = targetQuestion.responseDetails?.subQuestionResponses ?? [];

  const nextSubResponses = mergeSubResponsesForCache(
    previousSubResponses,
    patch.subQuestionId,
    patch.result,
    patch.respondedBy,
    now,
  );

  const derivedNextMain = deriveParentResult(nextSubResponses, patch.subQuestionRefs);
  const previousCompleted = deriveParentCompletion(
    previousSubResponses,
    patch.subQuestionRefs,
  );
  const nextCompleted = deriveParentCompletion(nextSubResponses, patch.subQuestionRefs);

  // Status reflects structural completion; result is committed
  // only when every scored child is answered (see deriveParentResult
  // — V1-aligned: no aggregate until complete).
  const nextMainResponse = {
    ...(previousMainResponse ?? EMPTY_RESPONSE),
    responseStatus: nextCompleted
      ? AuditQuestionCompletionStatus.COMPLETED
      : AuditQuestionCompletionStatus.IN_PROGRESS,
    responseResult: derivedNextMain,
    respondedBy: patch.respondedBy,
    respondedAt: now,
  } as unknown as QuestionResponseDetails;

  return buildNextCacheData(
    cachedData,
    targetIndex,
    nextMainResponse,
    nextSubResponses,
    applyMetricsDiff(
      cachedData.audit.metrics,
      previousMainResult,
      derivedNextMain,
      previousCompleted,
      nextCompleted,
    ),
  );
}

/** Replace or append the target sub entry with the new answer. */
function mergeSubResponsesForCache(
  existing: readonly SubQuestionResponseEntry[],
  targetSubQuestionId: string,
  result: ResponseResult,
  respondedBy: string,
  respondedAt: Date,
): SubQuestionResponseEntry[] {
  const targetIndex = existing.findIndex(
    (entry) => entry.subQuestionId === targetSubQuestionId,
  );
  const previousTargetResponse =
    targetIndex >= 0 ? existing.at(targetIndex)?.response : undefined;

  const nextTargetResponse: QuestionResponseDetails = {
    ...(previousTargetResponse ?? EMPTY_RESPONSE),
    responseStatus: AuditQuestionCompletionStatus.COMPLETED,
    responseResult: result,
    respondedBy,
    respondedAt,
  };
  if (result !== ResponseResult.NA) {
    delete (nextTargetResponse as Partial<QuestionResponseDetails>).naReason;
  }

  if (targetIndex >= 0) {
    return existing.map((entry, i) =>
      i === targetIndex ? { ...entry, response: nextTargetResponse } : entry,
    );
  }
  return [
    ...existing,
    { subQuestionId: targetSubQuestionId, response: nextTargetResponse },
  ];
}

export interface BulkAnswerPatch {
  /** Definitive child id + responseType list from the full-question
   *  fetch. The bulk result is stamped onto the SCORED subset only;
   *  data-collection entries are preserved untouched, and parent
   *  completion derives from the full list. */
  readonly subQuestionRefs: readonly SubQuestionTypeRef[];
  readonly result: ResponseResult;
  readonly respondedBy: string;
  readonly respondedAt?: Date;
}

/**
 * Patch a bulk Pass-All / Fail-All / NA-All: every SCORED child gets
 * the result, the parent takes it directly (uniform scored children
 * by construction). Data-collection children keep their captured
 * values; the parent's completion status reflects whether they are
 * all valued yet.
 */
export function snapshotAndPatchAuditBulkAnswer(
  queryClient: QueryClient,
  auditId: string,
  questionId: string,
  patch: BulkAnswerPatch,
): CacheSnapshot[] {
  const matchingQueries = queryClient.getQueriesData<GetAuditSummaryCommandOutput>({
    predicate: auditSummaryPredicate(auditId),
  });
  const snapshots: CacheSnapshot[] = [];
  const now = patch.respondedAt ?? new Date();

  for (const [queryKey, cachedData] of matchingQueries) {
    const nextData = computeBulkAnswerPatch(cachedData, questionId, patch, now);
    if (!nextData) continue;
    snapshots.push({ queryKey, data: cachedData });
    queryClient.setQueryData(queryKey, nextData);
  }
  return snapshots;
}

function computeBulkAnswerPatch(
  cachedData: GetAuditSummaryCommandOutput | undefined,
  questionId: string,
  patch: BulkAnswerPatch,
  now: Date,
): GetAuditSummaryCommandOutput | null {
  if (!cachedData?.questions || !cachedData.audit) return null;
  const targetIndex = cachedData.questions.findIndex(
    (q) => q.questionDetails.questionId === questionId,
  );
  if (targetIndex < 0) return null;
  const targetQuestion = cachedData.questions.at(targetIndex);
  if (!targetQuestion) return null;

  const previousMainResponse = targetQuestion.responseDetails?.mainQuestionResponse;
  const previousMainResult = previousMainResponse?.responseResult;
  const previousSubResponses = targetQuestion.responseDetails?.subQuestionResponses ?? [];

  const nextSubResponses = bulkStampSubResponses(
    previousSubResponses,
    patch.subQuestionRefs,
    patch.result,
    patch.respondedBy,
    now,
  );

  const previousCompleted = deriveParentCompletion(
    previousSubResponses,
    patch.subQuestionRefs,
  );
  const nextCompleted = deriveParentCompletion(nextSubResponses, patch.subQuestionRefs);

  // Result is the uniform scored answer; status still tracks the
  // FULL child set — unvalued data-collection children hold the
  // parent at IN_PROGRESS even after a Pass All.
  const nextMainResponse: QuestionResponseDetails = {
    ...(previousMainResponse ?? EMPTY_RESPONSE),
    responseStatus: nextCompleted
      ? AuditQuestionCompletionStatus.COMPLETED
      : AuditQuestionCompletionStatus.IN_PROGRESS,
    responseResult: patch.result,
    respondedBy: patch.respondedBy,
    respondedAt: now,
  };
  if (patch.result !== ResponseResult.NA) {
    delete (nextMainResponse as Partial<QuestionResponseDetails>).naReason;
  }

  return buildNextCacheData(
    cachedData,
    targetIndex,
    nextMainResponse,
    nextSubResponses,
    applyMetricsDiff(
      cachedData.audit.metrics,
      previousMainResult,
      patch.result,
      previousCompleted,
      nextCompleted,
    ),
  );
}

/**
 * Stamp the bulk result onto every SCORED child row, rebuilding the
 * cached array in DEFINITION order (the id list the caller passed —
 * the audit-detail sub rows render entries directly, so cache order
 * is display order). Data-collection rows ride through untouched at
 * their definition position; entries not in the def list (orphans)
 * keep their relative order at the end.
 */
function bulkStampSubResponses(
  existing: readonly SubQuestionResponseEntry[],
  subQuestionRefs: readonly SubQuestionTypeRef[],
  result: ResponseResult,
  respondedBy: string,
  respondedAt: Date,
): SubQuestionResponseEntry[] {
  const stampOne = (
    prev: SubQuestionResponseEntry | undefined,
    subQuestionId: string,
  ): SubQuestionResponseEntry => {
    const nextResponse: QuestionResponseDetails = {
      ...(prev?.response ?? EMPTY_RESPONSE),
      responseStatus: AuditQuestionCompletionStatus.COMPLETED,
      responseResult: result,
      respondedBy,
      respondedAt,
    };
    if (result !== ResponseResult.NA) {
      delete (nextResponse as Partial<QuestionResponseDetails>).naReason;
    }
    return { ...(prev ?? { subQuestionId }), subQuestionId, response: nextResponse };
  };

  const entriesById = new Map(existing.map((entry) => [entry.subQuestionId, entry]));
  const scoredIdSet = new Set(scoredSubQuestionIds(subQuestionRefs));
  const next: SubQuestionResponseEntry[] = [];
  const consumedIds = new Set<string>();
  for (const ref of subQuestionRefs) {
    if (!ref.subQuestionId) continue;
    consumedIds.add(ref.subQuestionId);
    const prev = entriesById.get(ref.subQuestionId);
    if (scoredIdSet.has(ref.subQuestionId)) {
      next.push(stampOne(prev, ref.subQuestionId));
    } else if (prev) {
      next.push(prev);
    }
  }
  for (const entry of existing) {
    if (!consumedIds.has(entry.subQuestionId)) next.push(entry);
  }
  return next;
}

/** Rollback shares the summary-snapshot shape — one function serves all patches. */
export { rollbackAuditSummaryCache as rollbackAuditQuestionResponseCache } from './patchAuditSummaryCache';
